import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatCostUsd } from '../../utils/formatCost';
import { getApiBase } from '../../lib/apiBase';
import { getDashboardAuthHeaders } from '../../lib/dashboardSession';
import { useDataTablesAssets, getJQuery } from '../../hooks/useDataTablesAssets';
import { formatDate } from './formatters';
import PaginationBar from './PaginationBar';

function escapeHtml(text) {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildDetailHtml(session, messages, loading) {
  const parts = [];
  if (session.costBreakdown) {
    const b = session.costBreakdown;
    if (
      b.rawSeconds != null ||
      b.billableSeconds != null ||
      b.creditsUsed != null ||
      b.totalUsd != null
    ) {
      const rawSeconds = Number(b.rawSeconds);
      const billableSeconds = Number(b.billableSeconds);
      const creditsUsed = Number(b.creditsUsed);
      const totalUsd = Number(b.totalUsd);
      const segments = [];
      if (Number.isFinite(rawSeconds)) segments.push(`duration ${rawSeconds}s`);
      if (Number.isFinite(billableSeconds)) segments.push(`billable ${billableSeconds}s`);
      if (Number.isFinite(creditsUsed)) segments.push(`credits ${creditsUsed}`);
      if (Number.isFinite(totalUsd)) segments.push(`total ${formatCostUsd(totalUsd, b.currency || 'USD')}`);
      if (segments.length) {
        parts.push(
          `<p class="text-muted text-sm mb-2">Cost estimate: ${segments.join(', ')}</p>`
        );
      }
    } else {
      parts.push(
        `<p class="text-muted text-sm mb-2">Cost estimate: avatar ${b.avatarMinutes} min (${formatCostUsd(b.avatarCost)}), TTS ${b.ttsCharacters} chars (${formatCostUsd(b.ttsCost)}), LLM ~${b.llmTokensEst} tokens (${formatCostUsd(b.llmCost)})</p>`
      );
    }
  }
  if (loading) {
    parts.push('<p class="text-muted text-sm"><i class="fas fa-spinner fa-spin mr-1"></i> Loading messages…</p>');
  } else if (!messages?.length) {
    parts.push('<p class="text-muted text-sm mb-0">No messages for this session.</p>');
  } else {
    messages.forEach((m) => {
      const role = m.role === 'user' ? 'User' : 'Avatar';
      const cls = m.role === 'user' ? 'message-user' : '';
      parts.push(
        `<div class="message-bubble ${cls}"><span class="badge badge-light text-uppercase mb-1">${role}</span><div>${escapeHtml(m.content)}</div></div>`
      );
    });
  }
  return `<div class="p-3 bg-light">${parts.join('')}</div>`;
}

export default function ConversationsDataTable({
  dateFrom,
  dateTo,
  refreshToken,
  onError,
}) {
  const assetsReady = useDataTablesAssets();
  const tableRef = useRef(null);
  const dtRef = useRef(null);
  const filtersRef = useRef({ dateFrom, dateTo });
  const messageCacheRef = useRef({});
  const [meta, setMeta] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    loading: true,
  });

  filtersRef.current = { dateFrom, dateTo };

  const fetchMessages = useCallback(async (sessionId) => {
    if (messageCacheRef.current[sessionId]) {
      return messageCacheRef.current[sessionId];
    }
    const res = await fetch(
      `${getApiBase()}/api/dashboard/session-messages?sessionId=${encodeURIComponent(sessionId)}`,
      { headers: getDashboardAuthHeaders() }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
    const msgs = Array.isArray(data.messages) ? data.messages : [];
    messageCacheRef.current[sessionId] = msgs;
    return msgs;
  }, []);

  useEffect(() => {
    messageCacheRef.current = {};
  }, [refreshToken, dateFrom, dateTo]);

  useEffect(() => {
    const $ = getJQuery();
    if (!assetsReady || !$ || !tableRef.current) return undefined;

    const tableEl = tableRef.current;
    const $table = $(tableEl);

    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $table.DataTable().clear().destroy();
    }

    const dt = $table.DataTable({
      processing: true,
      serverSide: true,
      responsive: true,
      autoWidth: false,
      pageLength: 20,
      lengthMenu: [10, 20, 50, 100],
      order: [[1, 'desc']],
      paging: true,
      dom:
        '<"row mb-2"<"col-sm-6"l><"col-sm-6"f>>' +
        '<"row"<"col-sm-12"tr>>',
      language: {
        emptyTable: 'No conversations yet. Complete a session on the avatar page to save one.',
        processing: '<i class="fas fa-spinner fa-spin"></i> Loading…',
      },
      ajax: (dtParams, callback) => {
        setMeta((m) => ({ ...m, loading: true }));
        const page = Math.floor(dtParams.start / dtParams.length) + 1;
        const pageSize = dtParams.length;
        const { dateFrom: from, dateTo: to } = filtersRef.current;

        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        });
        if (from) params.set('dateFrom', from);
        if (to) params.set('dateTo', to);

        fetch(`${getApiBase()}/api/dashboard/sessions?${params}`, {
          headers: getDashboardAuthHeaders(),
        })
          .then((res) => res.json().then((json) => ({ res, json })))
          .then(({ res, json }) => {
            if (!res.ok) {
              onError(json.error || `Error ${res.status}`);
              setMeta({ page: 1, pageSize: 20, total: 0, totalPages: 0, loading: false });
              callback({
                draw: dtParams.draw,
                recordsTotal: 0,
                recordsFiltered: 0,
                data: [],
              });
              return;
            }
            const rows = Array.isArray(json.sessions) ? json.sessions : [];
            const total = json.total ?? rows.length;
            const totalPages = json.totalPages ?? Math.max(1, Math.ceil(total / pageSize));
            setMeta({ page, pageSize, total, totalPages, loading: false });
            callback({
              draw: dtParams.draw,
              recordsTotal: total,
              recordsFiltered: total,
              data: rows,
            });
          })
          .catch((err) => {
            onError(err?.message || 'Failed to load conversations');
            setMeta({ page: 1, pageSize: 20, total: 0, totalPages: 0, loading: false });
            callback({
              draw: dtParams.draw,
              recordsTotal: 0,
              recordsFiltered: 0,
              data: [],
            });
          });
      },
      columns: [
        {
          data: 'avatarName',
          title: 'Avatar',
          render: (name, type, row) => {
            const label = escapeHtml(name || 'Avatar');
            const badge = row.endedAt
              ? ''
              : ' <span class="badge badge-warning ml-1">In progress</span>';
            return `<strong>${label}</strong>${badge}`;
          },
        },
        {
          data: 'startedAt',
          title: 'Started',
          render: (iso, type, row) => {
            let html = formatDate(iso);
            if (row.endedAt) {
              html += `<br><span class="text-muted small">→ ${formatDate(row.endedAt)}</span>`;
            }
            return html;
          },
        },
        {
          data: 'sessionCostUsd',
          title: 'Cost',
          className: 'text-success font-weight-bold',
          render: (cost) => formatCostUsd(cost),
        },
        {
          data: 'messageCount',
          title: 'Msgs',
          className: 'text-center',
          render: (n) => n ?? 0,
        },
        {
          data: null,
          title: '',
          orderable: false,
          className: 'text-center',
          defaultContent: '<i class="fas fa-chevron-down text-muted"></i>',
        },
      ],
      rowCallback(row, data) {
        $(row).addClass('session-toggle').css('cursor', 'pointer');
        $(row).attr('data-session-id', data.sessionId);
      },
    });

    dtRef.current = dt;

    $table.on('click', 'tbody tr', async function onRowClick() {
      const row = dt.row(this);
      const data = row.data();
      if (!data?.sessionId) return;

      if (row.child.isShown()) {
        row.child.hide();
        $(this).find('i.fas').removeClass('fa-chevron-up').addClass('fa-chevron-down');
        return;
      }

      dt.rows().every(function hideOthers() {
        if (this.child.isShown()) {
          this.child.hide();
          $(this.node()).find('i.fas').removeClass('fa-chevron-up').addClass('fa-chevron-down');
        }
        return true;
      });

      $(this).find('i.fas').removeClass('fa-chevron-down').addClass('fa-chevron-up');
      row.child(buildDetailHtml(data, null, true)).show();

      try {
        const messages = await fetchMessages(data.sessionId);
        row.child(buildDetailHtml(data, messages, false)).show();
      } catch (err) {
        row.child(
          `<div class="p-3 bg-light text-danger text-sm">${escapeHtml(err.message || 'Failed to load messages')}</div>`
        ).show();
      }
    });

    return () => {
      $table.off('click', 'tbody tr');
      if ($.fn.DataTable.isDataTable(tableEl)) {
        $table.DataTable().clear().destroy();
      }
      dtRef.current = null;
    };
  }, [assetsReady, dateFrom, dateTo, refreshToken, fetchMessages, onError]);

  const handlePageChange = (p) => {
    const dt = dtRef.current;
    if (!dt) return;
    dt.page(p - 1).draw('page');
  };

  const from = meta.total === 0 ? 0 : (meta.page - 1) * meta.pageSize + 1;
  const to = Math.min(meta.page * meta.pageSize, meta.total);

  if (!assetsReady) {
    return (
      <p className="text-center text-muted py-5 mb-0">
        <i className="fas fa-spinner fa-spin mr-2" />
        Loading table…
      </p>
    );
  }

  return (
    <>
      <table
        ref={tableRef}
        id="conversations-datatable"
        className="table table-bordered table-striped table-hover w-100 mb-0"
      >
        <thead>
          <tr>
            <th>Avatar</th>
            <th>Started</th>
            <th>Cost</th>
            <th>Msgs</th>
            <th />
          </tr>
        </thead>
        <tbody />
      </table>

      <div className="conversations-table-footer clearfix border-top bg-white px-2 py-2">
        <p className="text-muted small mb-2 mb-md-0 float-md-left pt-1">
          {meta.total > 0
            ? `Showing ${from} to ${to} of ${meta.total} entries`
            : 'No entries to show'}
        </p>
        <PaginationBar
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          pageSize={meta.pageSize}
          disabled={meta.loading}
          onPageChange={handlePageChange}
        />
      </div>
    </>
  );
}
